import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TestSummary, TestResult, Test, Question } from '../../types/test';
import './TestResults.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TestResults: React.FC = () => {
  const navigate = useNavigate();
  const [summaries, setSummaries] = useState<TestSummary[]>([]);
  const [selectedSummary, setSelectedSummary] = useState<TestSummary | null>(null);
  const [selectedResult, setSelectedResult] = useState<TestResult | null>(null);
  const [testData, setTestData] = useState<Test | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchTestSummaries = async () => {
    try {
      const response = await fetch(`${API_URL}/tests/results/summaries`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setSummaries(data.summaries || []);
      }
    } catch (error) {
      console.error('Error fetching test summaries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTestData = async (testId: string) => {
    try {
      const response = await fetch(`${API_URL}/tests/${testId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched test data:', data.test);
        console.log('Questions:', data.test?.questions);
        setTestData(data.test);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch test data:', response.status, response.statusText, errorText);
      }
    } catch (error) {
      console.error('Error fetching test data:', error);
    }
  };

  useEffect(() => {
    fetchTestSummaries();
  }, []);

  // Fetch test data when viewing individual result
  useEffect(() => {
    if (selectedResult) {
      fetchTestData(selectedResult.testId);
    } else {
      setTestData(null);
    }
  }, [selectedResult]);

  const handleSelectTest = async (testId: string) => {
    try {
      const response = await fetch(`${API_URL}/tests/results/${testId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedSummary(data.summary);
        setSelectedResult(null);
      }
    } catch (error) {
      console.error('Error fetching test summary:', error);
    }
  };

  if (loading) {
    return <div className="loading-container">Loading test results...</div>;
  }

  // View: Test Summary Dashboard
  if (!selectedSummary) {
    return (
      <div className="test-results">
        <div className="page-header">
          <button className="button-back" onClick={() => navigate('/admin/dashboard')}>
            ← Back to Dashboard
          </button>
          <div>
            <h1>Test Results</h1>
            <p>View test summaries and employee performance</p>
          </div>
        </div>

        <div className="summaries-grid">
          {summaries.length === 0 ? (
            <div className="empty-state">
              <p>No test results available yet.</p>
            </div>
          ) : (
            summaries.map((summary) => (
              <div
                key={summary.testId}
                className="summary-card"
                onClick={() => handleSelectTest(summary.testId)}
              >
                <h3>{summary.testTitle}</h3>
                <div className="summary-stats">
                  <div className="stat">
                    <span className="stat-label">Employees</span>
                    <span className="stat-value">{summary.totalEmployees}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Avg Score</span>
                    <span className="stat-value">{summary.averageScore.toFixed(1)}%</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Completion</span>
                    <span className="stat-value">{summary.completionRate.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // View: Test Summary Results (all employees)
  if (!selectedResult) {
    return (
      <div className="test-results">
        <div className="page-header">
          <button className="button-back" onClick={() => setSelectedSummary(null)}>
            ← Back to Results
          </button>
          <div>
            <h1>{selectedSummary.testTitle}</h1>
            <p>Test Summary Results</p>
          </div>
        </div>

        <div className="summary-overview">
          <div className="overview-stat">
            <span className="overview-label">Total Employees</span>
            <span className="overview-value">{selectedSummary.totalEmployees}</span>
          </div>
          <div className="overview-stat">
            <span className="overview-label">Average Score</span>
            <span className="overview-value">{selectedSummary.averageScore.toFixed(1)}%</span>
          </div>
          <div className="overview-stat">
            <span className="overview-label">Completion Rate</span>
            <span className="overview-value">{selectedSummary.completionRate.toFixed(1)}%</span>
          </div>
        </div>

        <div className="results-list">
          <h2>Employee Results</h2>
          {selectedSummary.results.map((result) => (
            <div
              key={`${result.testId}-${result.employeeId}`}
              className="result-card"
              onClick={() => setSelectedResult(result)}
            >
              <div className="result-header">
                <div>
                  <h3>{result.employeeName}</h3>
                  <p>{result.employeeEmail}</p>
                </div>
                <div className="result-score">
                  <span className="score-value">{result.percentage.toFixed(1)}%</span>
                  <span className="score-detail">
                    {result.score} / {result.totalPoints}
                  </span>
                </div>
              </div>
              <div className="result-meta">
                <span>Completed: {new Date(result.completedAt).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const getQuestionById = (questionId: string): Question | undefined => {
    return testData?.questions.find(q => q.id === questionId);
  };

  const formatCorrectAnswer = (question: Question): string => {
    if (!question.correctAnswer) return 'No correct answer specified';
    
    if (question.type === 'multiple-choice' && question.options) {
      // For multiple choice, show the correct option
      return question.correctAnswer.toString();
    } else if (question.type === 'true-false') {
      return question.correctAnswer.toString();
    } else {
      // For short-answer and essay
      return question.correctAnswer.toString();
    }
  };

  // View: Employee Test Summary Results (individual employee)
  return (
    <div className="test-results">
      <div className="page-header">
        <button className="button-back" onClick={() => {
          setSelectedResult(null);
          setTestData(null);
        }}>
          ← Back to Test Results
        </button>
        <div>
          <h1>{selectedResult.employeeName}'s Results</h1>
          <p>{selectedResult.testTitle}</p>
        </div>
      </div>

      <div className="employee-result">
        <div className="result-header-large">
          <div>
            <h2>{selectedResult.employeeName}</h2>
            <p>{selectedResult.employeeEmail}</p>
          </div>
          <div className="score-large">
            <span className="score-percentage">{selectedResult.percentage.toFixed(1)}%</span>
            <span className="score-breakdown">
              {selectedResult.score} out of {selectedResult.totalPoints} points
            </span>
          </div>
        </div>

        <div className="answers-list">
          <h3>Answers</h3>
          {!testData ? (
            <div className="loading-answers">Loading question details...</div>
          ) : (
            selectedResult.answers.map((answer, index) => {
              const question = getQuestionById(answer.questionId);
              const correctAnswer = question ? formatCorrectAnswer(question) : null;
              
              // Debug logging
              if (!question) {
                console.warn(`Question not found for ID: ${answer.questionId}`, {
                  availableQuestionIds: testData?.questions.map(q => q.id),
                  answerQuestionId: answer.questionId
                });
              } else {
                console.log(`Question found for ${answer.questionId}:`, {
                  questionText: question.questionText,
                  correctAnswer: question.correctAnswer,
                  formattedCorrectAnswer: correctAnswer,
                  isCorrect: answer.isCorrect
                });
              }
              
              return (
                <div
                  key={answer.questionId}
                  className={`answer-card ${answer.isCorrect ? 'correct' : 'incorrect'}`}
                >
                  <div className="answer-header">
                    <span className="answer-number">Q{index + 1}</span>
                    <span className={`answer-status ${answer.isCorrect ? 'correct' : 'incorrect'}`}>
                      {answer.isCorrect ? '✓ Correct' : '✗ Incorrect'} ({answer.pointsAwarded} pts)
                    </span>
                  </div>
                  {question && (
                    <div className="question-text-display">
                      <strong>Question:</strong> {question.questionText}
                    </div>
                  )}
                  <div className="answer-content">
                    <div className="employee-answer">
                      <strong>Employee's Answer:</strong> {typeof answer.answer === 'string' ? (answer.answer || '(No answer provided)') : answer.answer.join(', ')}
                    </div>
                    {!answer.isCorrect && correctAnswer && (
                      <div className="correct-answer-display">
                        <strong>Correct Answer:</strong> {correctAnswer}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="result-footer">
          <span>Completed: {new Date(selectedResult.completedAt).toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

export default TestResults;
